import { computed, Injectable, signal } from '@angular/core';
import { Observable, Subject } from 'rxjs';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastOptions {
  timeOut?: number;
  extendedTimeOut?: number;
  disableTimeOut?: boolean;
  closeButton?: boolean;
  tapToDismiss?: boolean;
  enableHtml?: boolean;
  positionClass?: string;
}

export interface ToastHandle {
  onTap: Observable<void>;
  update(message: string): void;
  close(): void;
}

export interface ApplicationToast {
  id: number;
  type: ToastType;
  message: string;
  title?: string;
  options: Required<ToastOptions>;
  onTapSubject: Subject<void>;
}

const DEFAULT_OPTIONS: Required<ToastOptions> = {
  timeOut: 5000,
  extendedTimeOut: 1000,
  disableTimeOut: false,
  closeButton: false,
  tapToDismiss: true,
  enableHtml: false,
  positionClass: 'toast-bottom-right',
};

@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly toastState = signal<ApplicationToast[]>([]);
  private readonly timers = new Map<number, ReturnType<typeof setTimeout>>();
  private nextId = 1;

  readonly toasts = this.toastState.asReadonly();
  readonly positions = computed(() => [...new Set(this.toastState().map(toast => toast.options.positionClass))]);

  success(message: string, title?: string, options?: ToastOptions): ToastHandle {
    return this.show('success', message, title, options);
  }

  error(message: string, title?: string, options?: ToastOptions): ToastHandle {
    return this.show('error', message, title, options);
  }

  warning(message: string, title?: string, options?: ToastOptions): ToastHandle {
    return this.show('warning', message, title, options);
  }

  info(message: string, title?: string, options?: ToastOptions): ToastHandle {
    return this.show('info', message, title, options);
  }

  close(id: number): void {
    this.clearTimer(id);
    this.toastState.update(toasts => toasts.filter(toast => toast.id !== id));
  }

  tap(toast: ApplicationToast): void {
    toast.onTapSubject.next();
    if (toast.options.tapToDismiss) this.close(toast.id);
  }

  pause(toast: ApplicationToast): void {
    if (!toast.options.disableTimeOut) this.clearTimer(toast.id);
  }

  resume(toast: ApplicationToast): void {
    if (!toast.options.disableTimeOut) this.scheduleClose(toast.id, toast.options.extendedTimeOut);
  }

  toastsAt(position: string): ApplicationToast[] {
    return this.toastState().filter(toast => toast.options.positionClass === position);
  }

  private show(type: ToastType, message: string, title?: string, options?: ToastOptions): ToastHandle {
    const onTapSubject = new Subject<void>();
    const toast: ApplicationToast = {
      id: this.nextId++,
      type,
      message: String(message ?? ''),
      title: title == null ? undefined : String(title),
      options: { ...DEFAULT_OPTIONS, ...options },
      onTapSubject,
    };

    this.toastState.update(toasts => [...toasts, toast]);
    if (!toast.options.disableTimeOut) this.scheduleClose(toast.id, toast.options.timeOut);

    return {
      onTap: onTapSubject.asObservable(),
      update: updatedMessage => this.update(toast.id, updatedMessage),
      close: () => this.close(toast.id),
    };
  }

  private update(id: number, message: string): void {
    this.toastState.update(toasts =>
      toasts.map(toast => (toast.id === id ? { ...toast, message: String(message ?? '') } : toast))
    );
  }

  private scheduleClose(id: number, delay: number): void {
    this.clearTimer(id);
    if (delay > 0) this.timers.set(id, setTimeout(() => this.close(id), delay));
  }

  private clearTimer(id: number): void {
    const timer = this.timers.get(id);
    if (timer !== undefined) clearTimeout(timer);
    this.timers.delete(id);
  }
}
